<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── SECTION HEADER ── -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Member Intelligence</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">Where Members <em>Go</em></h2>
      <p class="section-lede">
        The places reciprocal members already visit, in their own words — and where they'd want an official
        concierge arrangement. Useful when you land somewhere new. Leverage when we take it to the business.
      </p>

      <!-- ── AUDIENCE TABS ── -->
      <div class="tab-row">
        <button class="tab" :class="{ active: tab === 0 }" @click="tab = 0">All Members</button>
        <button class="tab" :class="{ active: tab === 1 }" @click="tab = 1">My Circle</button>
      </div>

      <!-- ── MOST REQUESTED STRIP ── -->
      <div v-if="!isLoading && topRequested.length" class="most-wanted">
        <div class="mw-header">
          <span class="mw-label">Most Wanted Arrangements{{ cityFilter ? ' · ' + cityFilter : '' }}</span>
          <span class="mw-sub">Ranked by members asking for an official arrangement</span>
        </div>
        <div class="mw-row">
          <div v-for="(place, i) in topRequested" :key="place.key" class="mw-card">
            <span class="mw-rank">{{ i + 1 }}</span>
            <div class="mw-body">
              <p class="mw-name">{{ place.businessName }}</p>
              <p class="mw-meta">{{ place.city }} · {{ place.category }}</p>
            </div>
            <div class="mw-count">
              <span class="mw-count-value">{{ place.count }}</span>
              <span class="mw-count-label">{{ place.count === 1 ? 'request' : 'requests' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── POST TYPE FILTER ── -->
      <div v-if="!isLoading" class="filter-block">
        <p class="filter-label">Show</p>
        <div class="chip-row">
          <button class="chip" :class="{ active: !postTypeFilter }" @click="postTypeFilter = ''">All Posts</button>
          <button class="chip" :class="{ active: postTypeFilter === 'recommendation' }" @click="postTypeFilter = postTypeFilter === 'recommendation' ? '' : 'recommendation'">Recommendations</button>
          <button class="chip" :class="{ active: postTypeFilter === 'travel_plan' }" @click="postTypeFilter = postTypeFilter === 'travel_plan' ? '' : 'travel_plan'">Travel Plans</button>
        </div>
      </div>

      <!-- ── CITY FILTER ── -->
      <div v-if="!isLoading && cities.length" class="filter-block">
        <p class="filter-label">City</p>
        <div class="chip-row">
          <button class="chip" :class="{ active: !cityFilter }" @click="cityFilter = ''">All Cities</button>
          <button
            v-for="c in cities"
            :key="c"
            class="chip"
            :class="{ active: cityFilter === c }"
            @click="cityFilter = cityFilter === c ? '' : c"
          >{{ c }}</button>
        </div>
      </div>

      <!-- ── CATEGORY FILTER ── -->
      <div v-if="!isLoading && categories.length" class="filter-block">
        <p class="filter-label">Category</p>
        <div class="chip-row">
          <button class="chip" :class="{ active: !categoryFilter }" @click="categoryFilter = ''">All Categories</button>
          <button
            v-for="cat in categories"
            :key="cat"
            class="chip"
            :class="{ active: categoryFilter === cat }"
            @click="categoryFilter = categoryFilter === cat ? '' : cat"
          >{{ cat }}</button>
        </div>
      </div>

      <!-- ── FEED ── -->
      <div class="feed">

        <!-- Loading -->
        <div v-if="isLoading" class="loading-row">
          <div class="loading-seal">
            <div class="seal-ring"></div>
            <div class="seal-ring-dashed"></div>
            <div class="seal-diamond"></div>
          </div>
          <p class="loading-text">Curating member recommendations…</p>
        </div>

        <!-- Empty -->
        <div v-else-if="filteredPosts.length === 0" class="empty-state">
          <div class="empty-seal">
            <div class="seal-ring"></div>
            <div class="seal-ring-dashed"></div>
            <div class="seal-diamond"></div>
          </div>
          <p class="empty-title">Nothing here yet</p>
          <p class="empty-sub">
            {{ activePosts.length === 0
              ? (tab === 0 ? 'No recommendations shared with the network yet.' : 'No recommendations from your circle yet.')
              : 'No recommendations match these filters.' }}
          </p>
        </div>

        <!-- Posts -->
        <template v-for="post in filteredPosts">
          <TravelPlanCard
            v-if="post.postType === 'travel_plan'"
            :key="post.id"
            :post="post"
            :currentUserUid="currentUser && currentUser.uid"
            @delete-post="handleDeletePost"
            @edit-post="handleEditPost"
            @toggle-saved="handleToggleSaved"
            @report-post="handleReportPost"
          />
          <ExplorePostCard
            v-else
            :key="post.id"
            :post="post"
            :currentUserUid="currentUser && currentUser.uid"
            @request-arrangement="toggleArrangementInterest"
            @comment-post="openComments"
            @delete-post="handleDeletePost"
            @edit-post="handleEditPost"
            @report-post="handleReportPost"
          />
        </template>

      </div>
    </div>

    <!-- ── FAB ── -->
    <button class="fab" @click="ensureUserAndOpenDialog">+</button>

    <!-- ── CREATE POST DIALOG ── -->
    <PostCreateDialog
      :model-value="showCreate"
      @update:modelValue="showCreate = $event"
      :user="currentUser"
      :is-public="tab === 0"
      @post-created="handlePostCreated"
    />

    <!-- ── EDIT POST DIALOG ── -->
    <EditPostDialog
      v-if="editingPost"
      :model-value="showEdit"
      @update:modelValue="showEdit = $event"
      :post="editingPost"
      @post-updated="handlePostUpdated"
    />

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
      </button>
      <button class="nav-item" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
      </button>
      <button class="nav-item active" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
        <div class="nav-dot"></div>
      </button>
      <button class="nav-item" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
      </button>
      <button class="nav-item" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
      </button>
      <button class="nav-item" @click="$router.push('/member-settings')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
      </button>
    </nav>
  </div>
</template>

<script>
import ExplorePostCard from '@/components/ExplorePostCard.vue'
import TravelPlanCard from '@/components/TravelPlanCard.vue'
import PostCreateDialog from '@/components/PostCreateDialog.vue'
import EditPostDialog from '@/components/EditPostDialog.vue'
import { db, auth } from '@/firebase'
import {
  collection, query, where, orderBy, getDocs,
  updateDoc, deleteDoc, getDoc, doc, addDoc, serverTimestamp,
} from 'firebase/firestore'

// Same rule as HomeView.vue's Travel Intelligence widget — a travel plan
// post disappears once the trip is over. A real return date is
// authoritative; without one (the field is optional, and older posts
// predate it existing), it's hidden this many days after posting instead
// of showing forever. Kept in sync with HomeView.vue's version.
const TRAVEL_PLAN_AUTO_EXPIRE_DAYS = 7;

function hasTravelDatePassed(post) {
  const now = new Date();

  if (post.travelEndDate) {
    const end = post.travelEndDate?.toDate
      ? post.travelEndDate.toDate()
      : new Date(post.travelEndDate);
    return now > end;
  }

  const posted = post.timestamp?.toDate
    ? post.timestamp.toDate()
    : (post.timestamp ? new Date(post.timestamp) : null);
  if (!posted) return false;

  const ageMs = now - posted;
  return ageMs > TRAVEL_PLAN_AUTO_EXPIRE_DAYS * 24 * 60 * 60 * 1000;
}

function isTravelPlanExpired(post) {
  // Used for filtering/hiding only — see the identical comment in
  // SearchView.vue / HomeView.vue. This was previously missing the
  // saved check, hiding saved travel plans from Explore entirely.
  return hasTravelDatePassed(post) && post.saved !== true;
}

export default {
  name: 'ExploreView',
  components: { ExplorePostCard, TravelPlanCard, PostCreateDialog, EditPostDialog },

  data() {
    return {
      tab: 0,
      publicPosts: [],
      friendsPosts: [],
      showCreate: false,
      showEdit: false,
      editingPost: null,
      currentUser: null,
      isLoading: true,
      cityFilter: '',
      categoryFilter: '',
      postTypeFilter: '',
      nowTick: Date.now(), // ticks periodically so expired travel plans disappear live, not just on next reload
    }
  },

  computed: {
    activePosts() {
      return this.tab === 0 ? this.publicPosts : this.friendsPosts
    },

    filteredPosts() {
      this.nowTick // eslint-disable-line no-unused-expressions
      return this.activePosts.filter(p => {
        if (p.postType === 'travel_plan' && isTravelPlanExpired(p)) return false
        if (this.postTypeFilter && (p.postType || 'recommendation') !== this.postTypeFilter) return false
        if (this.cityFilter && p.city !== this.cityFilter) return false
        if (this.categoryFilter && p.category !== this.categoryFilter) return false
        return true
      })
    },

    cities() {
      const set = new Set(this.activePosts.map(p => p.city).filter(Boolean))
      return [...set].sort()
    },

    categories() {
      // Only recommendation posts have categories — travel plans are excluded naturally
      const set = new Set(this.activePosts.map(p => p.category).filter(Boolean))
      return [...set].sort()
    },

    // The leverage data: which businesses have the most members asking for an
    // official arrangement, optionally scoped to the selected city.
    // Travel-plan posts have no businessName and are excluded automatically.
    topRequested() {
      const pool = this.cityFilter
        ? this.activePosts.filter(p => p.city === this.cityFilter)
        : this.activePosts

      const byPlace = new Map()
      pool.forEach(p => {
        if (!p.businessName) return
        const key = `${p.city || ''}::${p.businessName}`.toLowerCase()
        const existing = byPlace.get(key)
        const count = existing ? existing.count + (p.arrangementCount || 0) : (p.arrangementCount || 0)
        byPlace.set(key, {
          key,
          businessName: p.businessName,
          city: p.city || 'Unknown City',
          category: p.category || 'Recommendation',
          count,
        })
      })

      return [...byPlace.values()]
        .filter(p => p.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
    },
  },

  async mounted() {
    if (this.$route?.query?.city) {
      this.cityFilter = this.$route.query.city
    }
    await this.fetchUser()
    await Promise.all([this.fetchPosts(true), this.fetchPosts(false)])
    this.isLoading = false
    this._expireTickInterval = setInterval(() => { this.nowTick = Date.now() }, 15000)
  },

  beforeUnmount() {
    clearInterval(this._expireTickInterval)
  },

  methods: {
    async fetchUser() {
      const user = auth.currentUser
      if (!user) return
      this.currentUser = { uid: user.uid, fullName: user.displayName || '', image: user.photoURL || '' }
      if (!this.currentUser.fullName || !this.currentUser.image) {
        try {
          const q = query(collection(db, 'user'), where('uid', '==', user.uid))
          const snap = await getDocs(q)
          if (!snap.empty) {
            const data = snap.docs[0].data()
            this.currentUser.fullName = data.fullName || 'Anonymous'
            this.currentUser.image = data.image || ''
          }
        } catch (err) {
          console.error('Failed to fetch user profile:', err)
        }
      }
    },

    async ensureUserAndOpenDialog() {
      await this.fetchUser()
      if (!this.currentUser?.fullName || this.currentUser.fullName === 'Anonymous') {
        alert('Please complete your profile before posting.')
      } else {
        this.showCreate = true
      }
    },

    async fetchPosts(isPublic) {
      const uid = auth.currentUser?.uid
      if (!uid) return
      if (isPublic) {
        const q = query(collection(db, 'explore'), where('isPublic', '==', true), orderBy('timestamp', 'desc'))
        const snap = await getDocs(q)
        // groupOnly posts (created from a chapter's own composer) belong
        // only in that chapter's Top Recommendations/Traveling Now, never
        // the app-wide feed. Filtered client-side rather than as a
        // Firestore whereField: a boolean-equality query would exclude
        // every document written before this field existed (they have no
        // "groupOnly" key at all, not "false").
        const filteredDocs = snap.docs.filter(d => d.data().groupOnly !== true)
        this.publicPosts = await this.processPostSnapshots({ docs: filteredDocs }, uid)
      } else {
        const connSnap = await getDocs(collection(db, 'user', uid, 'connections'))
        const connectedUIDs = connSnap.docs.map(d => d.get('uid')).filter(Boolean)
        connectedUIDs.push(uid)
        const q = query(collection(db, 'explore'), where('isPublic', '==', false), orderBy('timestamp', 'desc'))
        const snap = await getDocs(q)
        const filteredDocs = snap.docs.filter(d => connectedUIDs.includes(d.get('uid')) && d.data().groupOnly !== true)
        this.friendsPosts = await this.processPostSnapshots({ docs: filteredDocs }, uid)
      }
    },

    async processPostSnapshots(snap, currentUid) {
      return await Promise.all(
        snap.docs.map(async docSnap => {
          const data = docSnap.data()
          const postId = docSnap.id
          const arrangementInterest = Array.isArray(data.arrangementInterest) ? data.arrangementInterest : []
          let commentCount = 0
          try {
            const commentsSnap = await getDocs(collection(db, 'explore', postId, 'comments'))
            commentCount = commentsSnap.size
          } catch (err) { console.error('Error fetching comments:', err) }

          let fullName = data.fullName
          let userImageURL = data.userImageURL
          if (!fullName || !userImageURL) {
            try {
              const userSnap = await getDoc(doc(db, 'user', data.uid))
              if (userSnap.exists()) {
                const ud = userSnap.data()
                fullName = ud.fullName || 'Anonymous'
                userImageURL = ud.image || ''
              }
            } catch (err) { console.error('Failed to fetch user for post:', err) }
          }

          const timestamp = data.timestamp?.toDate() || null
          return {
            id: postId, ...data, fullName, userImageURL,
            hasRequestedArrangement: arrangementInterest.includes(currentUid),
            arrangementCount: arrangementInterest.length,
            comments: commentCount,
            timestamp, timeAgo: timestamp ? this.getTimeAgo(timestamp) : '',
          }
        })
      )
    },

    getTimeAgo(date) {
      const diff = Math.floor((new Date() - date) / 1000)
      if (diff < 60) return 'just now'
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
      if (diff < 172800) return 'yesterday'
      return date.toLocaleDateString()
    },

    async toggleArrangementInterest(postID) {
      const postRef = doc(db, 'explore', postID)
      const postSnap = await getDoc(postRef)
      if (!postSnap.exists()) return
      const postData = postSnap.data()
      const interest = Array.isArray(postData.arrangementInterest) ? postData.arrangementInterest : []
      const uid = auth.currentUser?.uid
      if (!uid) return
      const wasRequesting = interest.includes(uid)
      const updated = wasRequesting ? interest.filter(id => id !== uid) : [...interest, uid]
      await updateDoc(postRef, { arrangementInterest: updated })

      // Notification is handled server-side by
      // sendArrangementInterestNotification in index.ts (via the Admin SDK,
      // which Firestore security rules can't block). A client-side write to
      // "notifications" used to happen here too, but rules correctly deny
      // client writes to that collection (only the post owner should ever
      // see who's asking, and only a Cloud Function should be able to write
      // it) — so it only ever produced a console error, never a real
      // notification. Removed the redundant attempt; same fix already
      // applied to PostDetail.vue's copy of this logic.

      await Promise.all([this.fetchPosts(true), this.fetchPosts(false)])
    },

    openComments(postID) {
      this.$router.push({ name: 'PostDetail', params: { postId: postID } })
    },

    async handleDeletePost(postId) {
      if (!window.confirm('Are you sure you want to remove this recommendation?')) return
      try {
        await deleteDoc(doc(db, 'explore', postId))
        await Promise.all([this.fetchPosts(true), this.fetchPosts(false)])
      } catch (err) { console.error('Failed to delete post:', err) }
    },

    async handleToggleSaved(post) {
      try {
        await updateDoc(doc(db, 'explore', post.id), { saved: !post.saved })
        await Promise.all([this.fetchPosts(true), this.fetchPosts(false)])
      } catch (err) { console.error('Failed to toggle saved state:', err) }
    },

    // Writes a standalone flags document rather than just a flag on the
    // post itself — a post can be deleted later (by its owner, or by an
    // admin acting on this very report) and the admin queue should still
    // show what was reported and why, so a snapshot of the post's content
    // is captured here rather than only a reference to it.
    async handleReportPost({ post, reason }) {
      if (!this.currentUser) return
      try {
        await addDoc(collection(db, 'flags'), {
          postId: post.id,
          postType: 'explore',
          postOwnerUid: post.uid || null,
          reporterUid: this.currentUser.uid,
          reason,
          status: 'pending',
          createdAt: serverTimestamp(),
          postSnapshot: {
            text: post.text || '',
            businessName: post.businessName || '',
            city: post.city || '',
            image: post.imageURL || (Array.isArray(post.media) && post.media[0]?.url) || null,
            fullName: post.fullName || '',
          },
        })
      } catch (err) {
        console.error('Failed to submit report:', err)
      }
    },

    handleEditPost(post) {
      this.editingPost = post
      this.showEdit = true
    },

    async handlePostUpdated() {
      this.showEdit = false
      this.editingPost = null
      await Promise.all([this.fetchPosts(true), this.fetchPosts(false)])
    },

    handlePostCreated(post) {
      if (post.groupOnly) return
      if (post.isPublic) this.publicPosts.unshift(post)
      else this.friendsPosts.unshift(post)
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 720px; margin: 0 auto; padding: 48px 24px 140px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── SECTION HEADER ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 40px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 14px; line-height: 1; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.section-lede { font-size: 13px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.4); max-width: 560px; margin-bottom: 32px; }

/* ── TABS ── */
.tab-row { display: flex; border-bottom: 1px solid rgba(210,175,100,0.12); margin-bottom: 32px; }
.tab { font-size: 10px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase; color: rgba(237,232,223,0.2); padding: 14px 28px 14px 0; margin-right: 28px; cursor: pointer; position: relative; background: none; border: none; font-family: 'Montserrat', sans-serif; transition: color 0.2s; }
.tab.active { color: rgba(210,175,100,0.8); }
.tab.active::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 28px; height: 1px; background: rgba(210,175,100,0.6); }
.tab:hover:not(.active) { color: rgba(237,232,223,0.45); }

/* ── MOST WANTED STRIP ── */
.most-wanted { border: 1px solid rgba(210,175,100,0.14); background: rgba(210,175,100,0.03); padding: 22px 24px; margin-bottom: 32px; position: relative; }
.most-wanted::before, .most-wanted::after { content: ''; position: absolute; width: 14px; height: 14px; }
.most-wanted::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.most-wanted::after { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.mw-header { display: flex; flex-direction: column; gap: 4px; margin-bottom: 18px; }
.mw-label { font-size: 9px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,175,100,0.7); }
.mw-sub { font-size: 10.5px; font-weight: 400; color: rgba(237,232,223,0.25); }
.mw-row { display: flex; flex-direction: column; gap: 0; }
.mw-card { display: flex; align-items: center; gap: 16px; padding: 12px 0; border-top: 1px solid rgba(210,175,100,0.07); }
.mw-card:first-child { border-top: none; }
.mw-rank { font-family: 'Cormorant', serif; font-size: 20px; font-weight: 300; color: rgba(210,175,100,0.4); width: 20px; flex-shrink: 0; }
.mw-body { flex: 1; min-width: 0; }
.mw-name { font-family: 'Cormorant', serif; font-size: 18px; font-weight: 400; color: #edeae2; line-height: 1.2; }
.mw-meta { font-size: 9px; font-weight: 500; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(237,232,223,0.22); margin-top: 2px; }
.mw-count { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
.mw-count-value { font-family: 'Cormorant', serif; font-size: 22px; font-weight: 300; color: rgba(210,175,100,0.85); line-height: 1; }
.mw-count-label { font-size: 7px; font-weight: 600; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(237,232,223,0.2); }

/* ── FILTERS ── */
.filter-block { margin-bottom: 20px; }
.filter-label { font-size: 8.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(237,232,223,0.2); margin-bottom: 10px; }
.chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
.chip { font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(237,232,223,0.35); background: transparent; border: 1px solid rgba(210,175,100,0.15); padding: 7px 13px; cursor: pointer; transition: color 0.2s, border-color 0.2s, background 0.2s; }
.chip:hover { border-color: rgba(210,175,100,0.4); color: rgba(237,232,223,0.6); }
.chip.active { color: rgba(210,175,100,0.9); border-color: rgba(210,175,100,0.6); background: rgba(210,175,100,0.08); }

/* ── FEED ── */
.feed { display: flex; flex-direction: column; gap: 20px; margin-top: 12px; }

/* ── LOADING / EMPTY ── */
.loading-row { display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 80px 0; }
.loading-seal, .empty-seal { position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 6s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.15); animation: spinSlow 4s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 12px; height: 12px; border: 1px solid rgba(210,175,100,0.6); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.3); }
.loading-text { font-size: 10px; font-weight: 500; letter-spacing: 3px; color: rgba(237,232,223,0.2); text-transform: uppercase; }
.empty-state { display: flex; flex-direction: column; align-items: center; padding: 80px 0; border: 1px solid rgba(210,175,100,0.08); text-align: center; }
.empty-title { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 300; color: rgba(237,232,223,0.3); margin-bottom: 10px; }
.empty-sub { font-size: 11px; font-weight: 400; letter-spacing: 1.5px; color: rgba(237,232,223,0.15); text-transform: uppercase; padding: 0 24px; }

/* ── FAB ── */
.fab { position: fixed; bottom: 88px; right: 28px; width: 48px; height: 48px; border: 1px solid rgba(210,175,100,0.4); background: rgba(7,7,8,0.92); color: rgba(210,175,100,0.7); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 24px; font-weight: 300; z-index: 30; transition: border-color 0.2s, color 0.2s; line-height: 1; }
.fab:hover { border-color: rgba(210,175,100,0.8); color: rgba(210,175,100,1); }

/* ── BOTTOM NAV ── */
.bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 20; background: #070708; border-top: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: space-around; padding: 14px 0 18px; }
.bottom-nav-glow { position: absolute; top: -1px; left: 10%; right: 10%; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent); pointer-events: none; }
.nav-item { display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; padding: 4px 20px; color: rgba(237,232,223,0.2); transition: color 0.2s; background: none; border: none; font-family: 'Montserrat', sans-serif; min-width: 60px; }
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
.nav-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.7); margin-top: 1px; }

@media (max-width: 600px) { .page { padding: 32px 16px 120px; } .frame, .frame-inner { inset: 12px; } .section-title { font-size: 32px; } .nav-item { padding: 4px 12px; } .mw-card { gap: 10px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>